/**
 * Capacity Sync API Service
 *
 * API functions for triggering capacity sync and viewing sync job history.
 * Uses Warehouse API for sync operations.
 */

import { ResponseArray, ResponseObject } from '@/types/shared'
import warehouse from '@/utils/axios/warehouse'
import type { SyncJob, SyncResult } from '../types/capacity'

/**
 * Trigger capacity sync from ServiceMgmt to Warehouse
 * @returns Sync job result with job ID
 */
export const triggerSync = async (): Promise<SyncResult> => {
    const response =
        await warehouse.post<ResponseObject<SyncResult>>('/capacity/sync')
    return response.data.result
}

/**
 * Trigger capacity sync for a specific zone
 * @param zoneId Zone UUID
 * @returns Sync job result with job ID
 */
export const triggerZoneSync = async (zoneId: string): Promise<SyncResult> => {
    const response = await warehouse.post<ResponseObject<SyncResult>>(
        `/capacity/zones/${zoneId}/sync`
    )
    return response.data.result
}

/**
 * Get sync job history
 * @param limit Maximum number of jobs to return (default 10)
 * @returns List of sync jobs sorted by created_at desc
 */
export const getSyncJobs = async (limit: number = 10): Promise<SyncJob[]> => {
    const response = await warehouse.get<ResponseArray<SyncJob>>(
        '/capacity/jobs',
        { params: { limit } }
    )
    return response.data.results
}

/**
 * Get the most recent sync job
 * @returns Most recent sync job or null if none
 */
export const getLatestSyncJob = async (): Promise<SyncJob | null> => {
    const jobs = await getSyncJobs(1)
    return jobs.length > 0 ? jobs[0] : null
}
